// assets/media-gallery.js
if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.productSection = this.closest('[id^="shopify-section-"]');

        this.elements = {
          mains: this.querySelectorAll(".product-media-main"),
          thumbnails: this.querySelectorAll(".product-media-thumbnail"),
          slideshow: this.querySelector(".product-media-main-wrapper"),
        };

        this.variantMediaMap = this.buildVariantMediaMap();

        if (!this.elements.thumbnails.length) {
          return;
        }

        this.filterThumbnailsByVariantMedia();

        this.elements.thumbnails.forEach((thumbnail) => {
          const mediaToggle = thumbnail.querySelector("button");
          if (!mediaToggle) {
            return;
          }

          mediaToggle.addEventListener("click", (event) => {
            event.preventDefault();

            const mediaId = mediaToggle.dataset.mediaId;
            if (!mediaId) {
              return;
            }

            this.setActiveMedia(mediaId, false);
            this.syncVariantFromMedia(mediaId);
          });
        });
      }

      connectedCallback() {
        if (!window.Flickity || !this.elements.slideshow) {
          return;
        }

        this.elements.slideshow.style.display = "block";
        this.flickity = new Flickity(this.elements.slideshow, {
          autoPlay: false,
          cellAlign: "center",
          groupCells: false,
          contain: true,
          resize: true,
          draggable: true,
          prevNextButtons: false,
          cellSelector: ".product-media",
          initialIndex: 0,
          pageDots: false,
          wrapAround: true,
          accessibility: false,
          adaptiveHeight: true,
          watchCSS: true,
          on: {
            change: (index) => {
              const cell = this.flickity?.cells?.[index];
              const mediaId = cell?.element?.dataset?.mediaId;

              if (!mediaId) {
                return;
              }

              this.setActiveMedia(mediaId);
              this.syncVariantFromMedia(mediaId);
            },
          },
        });
      }

      buildVariantMediaMap() {
        const map = new Map();
        const variantPicker = this.productSection?.querySelector(
          "variant-radios, variant-selects",
        );

        if (!variantPicker) {
          return map;
        }

        const resources = variantPicker.querySelectorAll(
          'script[type="application/json"][data-resource]',
        );

        resources.forEach((resource) => {
          try {
            const variant = JSON.parse(resource.textContent || "{}");
            const variantId = String(variant?.id || "");
            const mediaId = String(variant?.featured_media?.id || "");

            if (!variantId || !mediaId) {
              return;
            }

            if (!map.has(mediaId)) {
              map.set(mediaId, variantId);
            }
          } catch (error) {
            // JSON parse failure should not break gallery behavior
          }
        });

        return map;
      }

      filterThumbnailsByVariantMedia() {
        if (!this.variantMediaMap.size) {
          return;
        }

        this.elements.thumbnails.forEach((thumbnail) => {
          const mediaId = String(thumbnail.dataset.mediaId || "");
          const isVariantMedia = this.variantMediaMap.has(mediaId);

          thumbnail.hidden = !isVariantMedia;
          thumbnail.classList.toggle(
            "product-media-thumbnail-hidden",
            !isVariantMedia,
          );
        });
      }

      syncVariantFromMedia(mediaId) {
        if (!this.productSection || !this.variantMediaMap.size) {
          return;
        }

        const variantId = this.variantMediaMap.get(String(mediaId));
        if (!variantId) {
          return;
        }

        const radioInput = this.productSection.querySelector(
          `input[data-variant-id="${variantId}"]`,
        );

        if (radioInput instanceof HTMLInputElement) {
          if (!radioInput.checked) {
            radioInput.checked = true;
            radioInput.dispatchEvent(new Event("change", { bubbles: true }));
          }
          return;
        }

        const optionElement = this.productSection.querySelector(
          `option[data-variant-id="${variantId}"]`,
        );

        if (optionElement instanceof HTMLOptionElement) {
          const selectElement = optionElement.closest("select");

          if (
            selectElement instanceof HTMLSelectElement &&
            selectElement.value !== optionElement.value
          ) {
            selectElement.value = optionElement.value;
            selectElement.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMainMedia = [...this.elements.mains].find((element) => {
          return element.classList.contains("is-active");
        });
        const nextMainMedia = [...this.elements.mains].find((element) => {
          return element.dataset.mediaId === mediaId.toString();
        });

        if (!nextMainMedia) {
          return;
        }

        const index = [...this.elements.mains].indexOf(nextMainMedia);

        if (this.flickity && this.flickity.selectedIndex !== index) {
          this.flickity.select(index);
        }

        this.elements.thumbnails.forEach((thumb) => {
          thumb.classList.remove("is-active");
        });

        const thumbnail = [...this.elements.thumbnails].find((element) => {
          return element.dataset.mediaId === mediaId.toString();
        });

        if (activeMainMedia) {
          activeMainMedia.classList.remove("is-active");
        }

        nextMainMedia.classList.add("is-active");

        if (thumbnail && thumbnail.hidden === false) {
          thumbnail.classList.add("is-active");
        }

        this.playActiveMedia(nextMainMedia);

        if (!thumbnail || thumbnail.hidden) {
          return;
        }

        if (prepend) {
          thumbnail.parentElement?.prepend(thumbnail);
        } else {
          thumbnail.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }

      playActiveMedia(activeItem) {
        if (typeof window.pauseAllMedia === "function") {
          window.pauseAllMedia();
        }

        const deferredMedia = activeItem.querySelector("deferred-media");

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }
    },
  );
}

if (!customElements.get("media-zoom")) {
  customElements.define(
    "media-zoom",
    class MediaZoom extends HTMLElement {
      constructor() {
        super();

        this.mode = this.getAttribute("data-mode");
        this.zoomScale = 2;

        this.original = null;
        this.zoomArea = null;
        this.previewArea = null;
        this.previewImage = null;
        this.lastEvent = null;
        this.modalOpener = null;

        this.onPointerEnter = this.onPointerEnter.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this.onWindowScroll = this.onWindowScroll.bind(this);
        this.onWindowResize = this.onWindowResize.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);
      }

      connectedCallback() {
        this.ensureZoomMarkup();

        this.original = this.querySelector("img");
        this.zoomArea = this.querySelector(".media-zoom-zoom-area");
        this.previewArea = this.querySelector(".media-zoom-preview-area");
        this.previewImage = this.previewArea?.querySelector("img") || null;
        this.modalOpener = this.closest("modal-opener");

        if (
          !this.original ||
          !this.zoomArea ||
          !this.previewArea ||
          !this.previewImage
        ) {
          return;
        }

        this.previewImage.src =
          this.getAttribute("data-zoom") ||
          this.original.currentSrc ||
          this.original.src;

        this.previewImage.alt = this.original.alt || "";
        this.previewImage.draggable = false;

        this.applyBaseStyles();

        this.addEventListener("pointerenter", this.onPointerEnter);
        this.addEventListener("pointermove", this.onPointerMove);
        this.addEventListener("pointerleave", this.onPointerLeave);
        this.addEventListener("click", this.onClickCapture, true);

        if (this.modalOpener) {
          this.modalOpener.addEventListener("click", this.onClickCapture, true);
        }

        window.addEventListener("scroll", this.onWindowScroll, {
          passive: true,
        });
        window.addEventListener("resize", this.onWindowResize);
      }

      disconnectedCallback() {
        this.removeEventListener("pointerenter", this.onPointerEnter);
        this.removeEventListener("pointermove", this.onPointerMove);
        this.removeEventListener("pointerleave", this.onPointerLeave);
        this.removeEventListener("click", this.onClickCapture, true);

        if (this.modalOpener) {
          this.modalOpener.removeEventListener(
            "click",
            this.onClickCapture,
            true,
          );
        }

        window.removeEventListener("scroll", this.onWindowScroll);
        window.removeEventListener("resize", this.onWindowResize);
      }

      ensureZoomMarkup() {
        if (!this.querySelector(".media-zoom-zoom-area")) {
          this.insertAdjacentHTML(
            "beforeend",
            '<div class="media-zoom-zoom-area" aria-hidden="true"></div>',
          );
        }

        if (!this.querySelector(".media-zoom-preview-area")) {
          this.insertAdjacentHTML(
            "beforeend",
            '<div class="media-zoom-preview-area" aria-hidden="true"><img alt=""></div>',
          );
        }
      }

      applyBaseStyles() {
        if (this.style.position !== "relative") {
          this.style.position = "relative";
        }

        if (this.zoomArea) {
          this.zoomArea.style.position = "absolute";
          this.zoomArea.style.left = "0";
          this.zoomArea.style.top = "0";
          this.zoomArea.style.display = "none";
          this.zoomArea.style.pointerEvents = "none";
          this.zoomArea.style.zIndex = "10";
          this.zoomArea.style.boxSizing = "border-box";
          this.zoomArea.style.border = "1px solid rgba(0, 0, 0, 0.28)";
          this.zoomArea.style.background = "rgba(0, 0, 0, 0.10)";
        }

        if (this.previewArea) {
          this.previewArea.style.position = "fixed";
          this.previewArea.style.display = "none";
          this.previewArea.style.pointerEvents = "none";
          this.previewArea.style.overflow = "hidden";
          this.previewArea.style.zIndex = "20";
          this.previewArea.style.backgroundColor =
            "rgb(var(--color-background))";
          this.previewArea.style.boxShadow =
            "inset 0 0 0 1px rgb(var(--color-border)), 0 1.2rem 3rem rgba(0, 0, 0, 0.12)";
        }

        if (this.previewImage) {
          this.previewImage.style.position = "absolute";
          this.previewImage.style.left = "0";
          this.previewImage.style.top = "0";
          this.previewImage.style.maxWidth = "none";
          this.previewImage.style.maxHeight = "none";
          this.previewImage.style.objectFit = "fill";
          this.previewImage.style.transformOrigin = "top left";
          this.previewImage.style.userSelect = "none";
          this.previewImage.style.webkitUserDrag = "none";
        }
      }

      isHoverZoomMode() {
        return (
          this.mode === "zoom_hover" || this.mode === "lightbox_zoom_hover"
        );
      }

      isFinePointer(event) {
        if (!event) {
          return false;
        }

        if (event.pointerType === "mouse" || event.pointerType === "pen") {
          return true;
        }

        return false;
      }

      onClickCapture(event) {
        if (this.mode !== "lightbox_zoom_hover") {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
      }

      onPointerEnter(event) {
        if (!this.isHoverZoomMode() || !this.isFinePointer(event)) {
          return;
        }

        this.lastEvent = event;
        this.showPreview();
        this.updateZoom(event);
      }

      onPointerMove(event) {
        if (!this.isHoverZoomMode() || !this.isFinePointer(event)) {
          return;
        }

        this.lastEvent = event;
        this.showPreview();
        this.updateZoom(event);
      }

      onPointerLeave(event) {
        if (!this.isHoverZoomMode() || !this.isFinePointer(event)) {
          return;
        }

        this.hidePreview();
      }

      onWindowScroll() {
        if (!this.isPreviewVisible()) {
          return;
        }

        this.positionPreviewArea();

        if (this.lastEvent) {
          this.updateZoom(this.lastEvent);
        }
      }

      onWindowResize() {
        if (!this.isPreviewVisible()) {
          return;
        }

        this.positionPreviewArea();

        if (this.lastEvent) {
          this.updateZoom(this.lastEvent);
        }
      }

      isPreviewVisible() {
        return !!this.previewArea && this.previewArea.style.display === "block";
      }

      showPreview() {
        if (!this.previewArea || !this.zoomArea) {
          return;
        }

        this.positionPreviewArea();
        this.zoomArea.classList.add("active");
        this.zoomArea.style.display = "block";
        this.previewArea.style.display = "block";
      }

      hidePreview() {
        if (!this.zoomArea || !this.previewArea || !this.previewImage) {
          return;
        }

        this.zoomArea.classList.remove("active");
        this.zoomArea.style.display = "none";
        this.zoomArea.style.width = "";
        this.zoomArea.style.height = "";
        this.zoomArea.style.transform = "";
        this.previewArea.style.display = "none";
        this.previewImage.style.transform = "";
      }

      positionPreviewArea() {
        if (!this.original || !this.previewArea || !this.previewImage) {
          return;
        }

        const rect = this.original.getBoundingClientRect();
        const mediaMainRect =
          this.closest(".product-media-main")?.getBoundingClientRect() || rect;

        const gap = 16;
        const viewportGap = 16;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let previewWidth = Math.round(rect.width);
        const previewHeight = Math.round(mediaMainRect.height);

        const availableRight =
          viewportWidth - mediaMainRect.right - gap - viewportGap;

        if (availableRight > 240) {
          previewWidth = Math.min(previewWidth, Math.round(availableRight));
        } else {
          previewWidth = Math.min(
            Math.round(rect.width * 0.8),
            Math.max(240, viewportWidth - viewportGap * 2),
          );
        }

        let left = Math.round(mediaMainRect.right + gap);

        if (left + previewWidth > viewportWidth - viewportGap) {
          left = Math.round(viewportWidth - previewWidth - viewportGap);
        }

        if (left < viewportGap) {
          left = viewportGap;
        }

        let top = Math.round(mediaMainRect.top);

        if (top + previewHeight > viewportHeight - viewportGap) {
          top = Math.max(
            viewportGap,
            Math.round(viewportHeight - previewHeight - viewportGap),
          );
        }

        this.previewArea.style.width = `${previewWidth}px`;
        this.previewArea.style.height = `${previewHeight}px`;
        this.previewArea.style.left = `${left}px`;
        this.previewArea.style.top = `${top}px`;

        this.previewImage.style.width = `${Math.round(rect.width * this.zoomScale)}px`;
        this.previewImage.style.height = `${Math.round(rect.height * this.zoomScale)}px`;
      }

      updateZoom(event) {
        if (
          !this.original ||
          !this.zoomArea ||
          !this.previewArea ||
          !this.previewImage
        ) {
          return;
        }

        const rect = this.original.getBoundingClientRect();

        if (!rect.width || !rect.height) {
          return;
        }

        const offsetX = Math.max(
          0,
          Math.min(event.clientX - rect.left, rect.width),
        );
        const offsetY = Math.max(
          0,
          Math.min(event.clientY - rect.top, rect.height),
        );

        const previewWidth = this.previewArea.clientWidth || rect.width;
        const previewHeight = this.previewArea.clientHeight || rect.height;

        const lensWidth = Math.max(40, previewWidth / this.zoomScale);
        const lensHeight = Math.max(40, previewHeight / this.zoomScale);

        const maxLensLeft = Math.max(0, rect.width - lensWidth);
        const maxLensTop = Math.max(0, rect.height - lensHeight);

        const lensLeft = Math.min(
          maxLensLeft,
          Math.max(0, offsetX - lensWidth / 2),
        );
        const lensTop = Math.min(
          maxLensTop,
          Math.max(0, offsetY - lensHeight / 2),
        );

        const ratioX = maxLensLeft > 0 ? lensLeft / maxLensLeft : 0;
        const ratioY = maxLensTop > 0 ? lensTop / maxLensTop : 0;

        const imageWidth = rect.width * this.zoomScale;
        const imageHeight = rect.height * this.zoomScale;

        const maxTranslateX = Math.max(0, imageWidth - previewWidth);
        const maxTranslateY = Math.max(0, imageHeight - previewHeight);

        const translateX = -(maxTranslateX * ratioX);
        const translateY = -(maxTranslateY * ratioY);

        this.zoomArea.classList.add("active");
        this.zoomArea.style.display = "block";
        this.zoomArea.style.width = `${lensWidth}px`;
        this.zoomArea.style.height = `${lensHeight}px`;
        this.zoomArea.style.transform = `translate(${lensLeft}px, ${lensTop}px)`;
        this.zoomArea.style.border = "1px solid rgba(0, 0, 0, 0.28)";
        this.zoomArea.style.background = "rgba(0, 0, 0, 0.10)";

        this.previewImage.style.transform = `translate(${translateX}px, ${translateY}px)`;
      }
    },
  );
}
